"use client";

import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { Button, Input, Label, Textarea, useToast, useUniversalAuth } from "@wtc/ui";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import {
  useCreateMspDocument,
  useUpdateMspDocument,
  MspDocument,
} from "@/lib/hooks/api/msp-docs/msp-docs-graphql";
import { useAdminUsers } from "@/lib/hooks/use-api";
import { documentSchema, type DocumentFormData } from "./msp-document-schema";
import {
  MSP_DOCUMENT_CATEGORIES,
  MSP_DOCUMENT_DIFFICULTIES,
} from "./msp-document-constants";
import {
  MspDocumentTagsSection,
  MspDocumentSettingsSection,
  MspDocumentSharingSection,
} from "./MspDocumentEditor/index";
import { MspContentEditor } from "./MspDocumentEditor/MspContentEditor";
import { AiDraftPanel } from "./MspDocumentEditor/AiDraftPanel";
import { AiAssistToolbar } from "./MspDocumentEditor/AiAssistToolbar";
import { CustomerContextFields } from "./MspDocumentEditor/CustomerContextFields";
import { WizardStepper, type WizardStep } from "./MspDocumentEditor/WizardStepper";

interface MspDocumentEditorProps {
  document?: MspDocument;
  defaultValues?: Partial<DocumentFormData>;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const STEPS: WizardStep[] = [
  { id: "basics", label: "Basics" },
  { id: "content", label: "Content" },
  { id: "organize", label: "Organize & share" },
  { id: "review", label: "Review" },
];

/** Fields validated before leaving each step (index-aligned with STEPS). */
const STEP_FIELDS: Array<Array<keyof DocumentFormData>> = [
  ["title", "category", "difficulty", "excerpt"],
  ["content"],
  ["client_id", "company_id", "tags", "estimated_read_time"],
  [],
];

/** Which step owns a given field — used to jump to the first error on submit. */
function stepForField(field: string): number {
  const idx = STEP_FIELDS.findIndex((fields) => (fields as string[]).includes(field));
  return idx === -1 ? 0 : idx;
}

export function MspDocumentEditor({
  document,
  defaultValues,
  onSuccess,
  onCancel,
}: MspDocumentEditorProps) {
  const [newTag, setNewTag] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const { toast } = useToast();
  const { user } = useUniversalAuth();

  const createMutation = useCreateMspDocument();
  const updateMutation = useUpdateMspDocument();
  const { data: allUsers } = useAdminUsers({ is_active: true, pageSize: 1000 });

  const isEditing = !!document;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<DocumentFormData>({
    resolver: zodResolver(documentSchema),
    mode: "onTouched",
    defaultValues: {
      title: document?.title || defaultValues?.title || "",
      content: document?.content || defaultValues?.content || "",
      excerpt: document?.excerpt || defaultValues?.excerpt || "",
      category: (document?.category as DocumentFormData["category"]) || defaultValues?.category || "procedures",
      tags: (document?.tags as string[]) || defaultValues?.tags || [],
      client_id: document?.client_id || defaultValues?.client_id || "",
      company_id: document?.company_id || defaultValues?.company_id || "",
      difficulty: (document?.difficulty as DocumentFormData["difficulty"]) || defaultValues?.difficulty || "beginner",
      is_internal: document?.is_internal ?? defaultValues?.is_internal ?? true,
      is_featured: document?.is_featured ?? defaultValues?.is_featured ?? false,
      estimated_read_time: document?.estimated_read_time || defaultValues?.estimated_read_time,
      shared_with_users: (document?.shared_with_users as string[]) || defaultValues?.shared_with_users || [],
    },
  });

  const values = watch();
  const { tags, shared_with_users, content } = values;

  const goToStep = useCallback((index: number) => {
    setStepIndex(index);
    setMaxReached((prev) => Math.max(prev, index));
  }, []);

  const goNext = useCallback(async () => {
    const valid = await trigger(STEP_FIELDS[stepIndex]);
    if (!valid) return;
    goToStep(Math.min(stepIndex + 1, STEPS.length - 1));
  }, [trigger, stepIndex, goToStep]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // ---- tags -----------------------------------------------------------------
  const handleAddTag = useCallback(() => {
    const trimmed = newTag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setValue("tags", [...tags, trimmed]);
      setNewTag("");
    }
  }, [newTag, tags, setValue]);

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => setValue("tags", tags.filter((t) => t !== tagToRemove)),
    [tags, setValue]
  );

  const handleTagKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  // ---- sharing --------------------------------------------------------------
  const handleAddUser = useCallback(
    async (userId: string) => {
      if (!shared_with_users.includes(userId)) {
        setValue("shared_with_users", [...shared_with_users, userId]);
      }
    },
    [shared_with_users, setValue]
  );

  const handleRemoveUser = useCallback(
    (userId: string) => setValue("shared_with_users", shared_with_users.filter((id) => id !== userId)),
    [shared_with_users, setValue]
  );

  // ---- AI --------------------------------------------------------------------
  const applyDraft = useCallback(
    (draft: { content: string; excerpt?: string; tags?: string[] }) => {
      setValue("content", draft.content, { shouldValidate: true });
      if (draft.excerpt && !values.excerpt?.trim()) {
        setValue("excerpt", draft.excerpt.slice(0, 200));
      }
      if (draft.tags?.length) {
        const merged = Array.from(new Set([...tags, ...draft.tags]));
        setValue("tags", merged);
      }
      toast({ title: "Draft ready", description: "Review and edit before saving." });
    },
    [setValue, values.excerpt, tags, toast]
  );

  // ---- submit ----------------------------------------------------------------
  const onSubmit = async (data: DocumentFormData) => {
    setIsSubmitting(true);
    try {
      const cleanedData = {
        title: data.title?.trim(),
        content: data.content?.trim(),
        excerpt: data.excerpt?.trim() || null,
        category: data.category,
        tags: Array.isArray(data.tags) ? data.tags.filter((t) => t?.trim()) : [],
        client_id: data.client_id || null,
        company_id: data.company_id || null,
        difficulty: data.difficulty || "beginner",
        is_internal: Boolean(data.is_internal),
        is_featured: Boolean(data.is_featured),
        estimated_read_time: data.estimated_read_time ? Number(data.estimated_read_time) : null,
        shared_with_users: Array.isArray(data.shared_with_users)
          ? data.shared_with_users.filter((id) => id?.trim())
          : [],
        ...(isEditing ? { updated_by: user?.id } : { created_by: user?.id }),
      };

      if (!user?.id) throw new Error("User authentication required");

      if (isEditing) {
        await updateMutation.mutateAsync({ id: document.id, input: cleanedData });
      } else {
        await createMutation.mutateAsync(cleanedData);
      }
      onSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Failed to save document");
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSubmit(onSubmit, (formErrors) => {
      // Jump to the earliest step with an error so the user can see it.
      const firstErrorStep = Object.keys(formErrors)
        .map(stepForField)
        .sort((a, b) => a - b)[0];
      if (firstErrorStep !== undefined) goToStep(firstErrorStep);
      toast({
        title: "Please finish the required fields",
        description: "Some required fields still need attention.",
        variant: "destructive",
      });
    })(e);
  };

  const categoryLabel = useMemo(
    () => MSP_DOCUMENT_CATEGORIES.find((c) => c.value === values.category)?.label ?? values.category,
    [values.category]
  );
  const difficultyLabel = useMemo(
    () => MSP_DOCUMENT_DIFFICULTIES.find((d) => d.value === values.difficulty)?.label ?? values.difficulty,
    [values.difficulty]
  );

  const isLastStep = stepIndex === STEPS.length - 1;
  const currentStepId = STEPS[stepIndex].id;

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      <WizardStepper
        steps={STEPS}
        currentIndex={stepIndex}
        maxReachedIndex={maxReached}
        onStepClick={goToStep}
      />

      {/* ---- Step: Basics ---- */}
      {currentStepId === "basics" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              {...register("title")}
              placeholder="Enter document title..."
              className={errors.title ? "border-destructive" : ""}
            />
            {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <ResponsiveCommandSelect
                value={values.category}
                onValueChange={(v) => setValue("category", v as DocumentFormData["category"], { shouldValidate: true })}
                options={MSP_DOCUMENT_CATEGORIES}
                placeholder="Select category"
                className={errors.category ? "border-destructive" : ""}
              />
              {errors.category && <p className="text-sm text-destructive">{errors.category.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="difficulty">Difficulty</Label>
              <ResponsiveCommandSelect
                value={values.difficulty}
                onValueChange={(v) => setValue("difficulty", v as DocumentFormData["difficulty"])}
                options={MSP_DOCUMENT_DIFFICULTIES}
                placeholder="Select difficulty"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              {...register("excerpt")}
              rows={3}
              placeholder="Brief description of the document (optional)..."
            />
          </div>
        </div>
      )}

      {/* ---- Step: Content ---- */}
      {currentStepId === "content" && (
        <div className="space-y-3">
          <AiDraftPanel
            title={values.title}
            category={values.category}
            difficulty={values.difficulty}
            hasContent={!!content?.trim()}
            onDraft={applyDraft}
          />
          <div className="flex items-center justify-between gap-2">
            <Label>Content *</Label>
            <AiAssistToolbar
              content={content || ""}
              onResult={(c) => setValue("content", c, { shouldValidate: true })}
            />
          </div>
          <MspContentEditor value={content || ""} onChange={(c) => setValue("content", c, { shouldValidate: true })} />
          {errors.content && <p className="text-sm text-destructive">{errors.content.message}</p>}
        </div>
      )}

      {/* ---- Step: Organize & share ---- */}
      {currentStepId === "organize" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <CustomerContextFields
              category={values.category}
              companyId={values.company_id}
              clientId={values.client_id}
              setValue={setValue}
            />
            <div className="space-y-2">
              <Label htmlFor="estimated_read_time">Estimated read time (minutes)</Label>
              <Input
                id="estimated_read_time"
                type="number"
                min={1}
                {...register("estimated_read_time")}
                placeholder="e.g., 5"
              />
            </div>
          </div>
          <div className="space-y-6">
            <MspDocumentTagsSection
              tags={tags}
              newTag={newTag}
              setNewTag={setNewTag}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              onKeyPress={handleTagKeyPress}
            />
            <MspDocumentSettingsSection
              isInternal={values.is_internal}
              isFeatured={values.is_featured}
              setValue={setValue}
            />
            <MspDocumentSharingSection
              sharedWithUsers={shared_with_users}
              allUsers={allUsers}
              onAddUser={handleAddUser}
              onRemoveUser={handleRemoveUser}
            />
          </div>
        </div>
      )}

      {/* ---- Step: Review ---- */}
      {currentStepId === "review" && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
          <ReviewRow label="Title" value={values.title || "—"} />
          <ReviewRow label="Category" value={categoryLabel} />
          <ReviewRow label="Difficulty" value={difficultyLabel} />
          <ReviewRow label="Visibility" value={values.is_internal ? "Internal only" : "Shared"} />
          <ReviewRow label="Tags" value={tags.length ? tags.join(", ") : "None"} />
          <ReviewRow
            label="Content"
            value={`${(content || "").trim().length} characters`}
          />
          {values.excerpt?.trim() && (
            <div className="sm:col-span-2">
              <ReviewRow label="Excerpt" value={values.excerpt} />
            </div>
          )}
        </dl>
      )}

      {/* ---- Footer ---- */}
      <div className="flex items-center justify-between gap-2 border-t pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          {stepIndex > 0 && (
            <Button type="button" variant="outline" onClick={goBack}>
              <ArrowLeft className="h-4 w-4" />
              <span className="ml-1">Back</span>
            </Button>
          )}
          {isLastStep ? (
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              <span className={isSubmitting ? "ml-1" : ""}>
                {isEditing ? "Update document" : "Create document"}
              </span>
            </Button>
          ) : (
            <Button type="button" onClick={goNext}>
              <span className="mr-1">Next</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm text-foreground" title={value}>
        {value}
      </dd>
    </div>
  );
}
